// src/features/incidents/ImageAnalysisPanel.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  PlayArrow,
  Visibility,
  Warning,
  Error,
  CheckCircle,
  Image,
  Analytics,
  Recommend,
  BugReport,
  Build
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';
import imageAnalysisEngine from './incidentsImageAnalysis';
import SafeImage from '../../components/SafeImage';

export default function ImageAnalysisPanel({ incident, onAnalysisComplete }) {
  const { t } = useI18n();
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [engineStats, setEngineStats] = useState(null);

  useEffect(() => {
    // Завантаження статистики рушія
    setEngineStats(imageAnalysisEngine.getStats());
  }, []);

  const handleAnalyzeImages = async () => {
    if (!incident?.id) return;

    setAnalyzing(true);
    try {
      const result = await imageAnalysisEngine.analyzeIncidentPhotos(incident.id);
      setAnalysisResult(result);
      
      if (onAnalysisComplete && result.success) {
        onAnalysisComplete(result);
      }
    } catch (error) {
      console.error('Помилка аналізу зображень:', error);
      setAnalysisResult({ 
        success: false, 
        error: error.message 
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      high: 'error',
      medium: 'warning',
      low: 'info',
      unknown: 'default'
    };
    return colors[severity] || 'default';
  };

  const getSeverityIcon = (severity) => {
    const icons = {
      high: <Error fontSize="small" />,
      medium: <Warning fontSize="small" />,
      low: <CheckCircle fontSize="small" />,
      unknown: <BugReport fontSize="small" />
    };
    return icons[severity] || <BugReport fontSize="small" />;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  const getSensorIcon = (sensorType) => {
    const icons = {
      temperature: <Image fontSize="small" />,
      vibration: <Build fontSize="small" />,
      pressure: <Analytics fontSize="small" />,
      humidity: <BugReport fontSize="small" />,
      power_consumption: <Warning fontSize="small" />
    };
    return icons[sensorType] || <Image fontSize="small" />;
  };

  if (!incident?.photos || incident.photos.length === 0) {
    return (
      <Alert severity="info">
        {t('image_analysis_no_photos') || 'Для аналізу необхідні фотографії інциденту'}
      </Alert>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* Заголовок та керування */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            🖼️ {t('image_analysis_title') || 'Аналіз зображень AI'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('image_analysis_subtitle') || 'Computer Vision аналіз фотографій інциденту'}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          {engineStats && (
            <Chip
              icon={<Analytics />}
              label={`v${engineStats.model?.version || '1.0.0'}`}
              variant="outlined"
              size="small"
            />
          )}
          
          <Button
            variant="contained"
            startIcon={analyzing ? <LinearProgress size={20} /> : <PlayArrow />}
            onClick={handleAnalyzeImages}
            disabled={analyzing}
          >
            {analyzing 
              ? (t('image_analysis_analyzing') || 'Аналіз...')
              : (t('image_analysis_start') || 'Запустити аналіз')
            }
          </Button>
        </Stack>
      </Stack>

      {/* Прогрес аналізу */}
      {analyzing && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            {t('image_analysis_processing') || 'Обробка зображень...'}
          </Typography>
        </Box>
      )}

      {/* Результати аналізу */}
      {analysisResult && (
        <Box sx={{ mb: 3 }}>
          {analysisResult.success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="h6">
                ✅ {t('image_analysis_success') || 'Аналіз успішно завершено!'}
              </Typography>
              <Typography>
                {t('image_analysis_photos_analyzed') || 'Проаналізовано'} {analysisResult.analyses.length} {t('image_analysis_photos') || 'фотографій'}
              </Typography>
            </Alert>
          ) : (
            <Alert severity="error">
              {t('image_analysis_failed') || 'Помилка аналізу:'} {analysisResult.error}
            </Alert>
          )}

          {/* Зведення аналізу */}
          {analysisResult.summary && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📊 {t('image_analysis_summary') || 'Зведення аналізу'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      {t('image_analysis_total_photos') || 'Всього фото'}
                    </Typography>
                    <Typography variant="h6">
                      {analysisResult.summary.totalPhotos}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      {t('image_analysis_anomalies') || 'Аномалій'}
                    </Typography>
                    <Typography variant="h6" color="warning.main">
                      {analysisResult.summary.totalAnomalies}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      {t('image_analysis_critical') || 'Критичних'}
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {analysisResult.summary.highSeverityAnomalies}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      {t('image_analysis_confidence') || 'Впевненість'}
                    </Typography>
                    <Typography variant="h6">
                      {Math.round(analysisResult.summary.confidence * 100)}%
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Список фотографій з аналізом */}
      <Typography variant="h6" gutterBottom>
        📷 {t('image_analysis_photos_list') || 'Фотографії інциденту'}
      </Typography>
      
      <Grid container spacing={2}>
        {incident.photos.map((photo, index) => {
          const photoAnalysis = analysisResult?.analyses?.find(a => a.photoId === photo.id);
          
          return (
            <Grid item xs={12} md={6} lg={4} key={photo.id}>
              <Card variant="outlined">
                <CardContent>
                  {/* Зображення */}
                  <Box 
                    sx={{ 
                      position: 'relative',
                      cursor: 'pointer',
                      mb: 2
                    }}
                    onClick={() => {
                      setSelectedImage(photo);
                      setImageDialogOpen(true);
                    }}
                  >
                    <SafeImage
                      src={photo.dataUrl}
                      alt={photo.name}
                      style={{
                        width: '100%',
                        height: 200,
                        borderRadius: 1,
                        objectFit: 'cover'
                      }}
                    />
                    <Tooltip title={t('image_analysis_view_large') || 'Переглянути у великому розмірі'}>
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(255,255,255,0.8)',
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.9)'
                          }
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Інформація про фото */}
                  <Typography variant="subtitle2" gutterBottom>
                    {photo.name}
                  </Typography>

                  {/* Результати аналізу */}
                  {photoAnalysis ? (
                    <Box>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <Chip
                          icon={getSeverityIcon(photoAnalysis.analysis.severity)}
                          label={t(`image_analysis_severity_${photoAnalysis.analysis.severity}`) || photoAnalysis.analysis.severity}
                          color={getSeverityColor(photoAnalysis.analysis.severity)}
                          size="small"
                        />
                        <Chip
                          label={`${Math.round(photoAnalysis.analysis.confidence * 100)}%`}
                          color={getConfidenceColor(photoAnalysis.analysis.confidence)}
                          variant="outlined"
                          size="small"
                        />
                      </Stack>

                      {/* Виявлені аномалії */}
                      {photoAnalysis.analysis.anomalies.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('image_analysis_detected_anomalies') || 'Виявлені аномалії:'}
                          </Typography>
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                            {photoAnalysis.analysis.anomalies.slice(0, 3).map((anomaly, idx) => (
                              <Chip
                                key={idx}
                                label={anomaly.type}
                                size="small"
                                variant="outlined"
                                color={getSeverityColor(anomaly.severity)}
                              />
                            ))}
                            {photoAnalysis.analysis.anomalies.length > 3 && (
                              <Chip
                                label={`+${photoAnalysis.analysis.anomalies.length - 3}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('image_analysis_not_analyzed') || 'Ще не проаналізовано'}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Рекомендації */}
      {analysisResult?.summary?.recommendations && analysisResult.summary.recommendations.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            💡 {t('image_analysis_recommendations') || 'Рекомендації AI'}
          </Typography>
          <List>
            {analysisResult.summary.recommendations.map((rec, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <Recommend color={rec.priority === 'high' ? 'error' : 'warning'} />
                </ListItemIcon>
                <ListItemText
                  primary={rec.action}
                  secondary={rec.reason}
                />
                <Chip
                  label={t(`image_analysis_priority_${rec.priority}`) || rec.priority}
                  color={rec.priority === 'high' ? 'error' : 'warning'}
                  size="small"
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Діалог перегляду зображення */}
      <Dialog 
        open={imageDialogOpen} 
        onClose={() => setImageDialogOpen(false)}
        maxWidth="lg"
      >
        <DialogTitle>
          {selectedImage?.name || t('image_analysis_image_view') || 'Перегляд зображення'}
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <SafeImage
              src={selectedImage.dataUrl}
              alt={selectedImage.name}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                width: 'auto',
                height: 'auto'
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>
            {t('common_close') || 'Закрити'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}